import React, { useEffect, useRef, useState, Suspense, lazy, useMemo } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Header from '../../components/layout/Header';
import UserSidebar from '../../components/dashboard/UserSidebar';
import Workplace from '../../components/dashboard/Workplace';
import CustomScrollbar from '../../components/common/CustomScrollbar';
import PageLoader from '../../components/common/PageLoader';
import ProtectedAdminRoute from '../../components/common/ProtectedAdminRoute';
import { useAuth } from '../../hooks/useAuth';
import { usersService, DashboardUser } from '../../services/usersService';
import { User, UserRole, Practice } from '../../types';
import './Dashboard.css';
import ClientsArchive from '../Clients/ClientsArchive';

// Lazy loading для всех страниц с предзагрузкой данных
const Clients = lazy(async () => {
  // Предзагружаем данные клиентов во время загрузки модуля
  const [module, { clientsKeys }, { queryClient }, { clientsService }] = await Promise.all([
    import('../Clients/Clients'),
    import('../../hooks/useClients'),
    import('../../config/queryClient'),
    import('../../services/clientsService'),
  ]);

  // Предзагружаем данные параллельно
  const filters = { page: 1, per_page: 100 };
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: clientsKeys.list(filters),
      queryFn: () => clientsService.getClients(filters),
      staleTime: 1000 * 30,
    }),
    queryClient.prefetchQuery({
      queryKey: clientsKeys.statistics(),
      queryFn: () => clientsService.getStatistics(),
      staleTime: 1000 * 30,
    }),
    queryClient.prefetchQuery({
      queryKey: clientsKeys.filterOptions(),
      queryFn: () => clientsService.getFilterOptions(),
      staleTime: 1000 * 60 * 5,
    }),
  ]).catch(() => {
    // Игнорируем ошибки предзагрузки - данные загрузятся при рендере
  });

  return module;
});
const ClientDetail = lazy(() => import('../Clients/ClientDetail/ClientDetail'));
const ClientForm = lazy(() => import('../Clients/ClientForm/ClientForm'));
const Projects = lazy(() => import('../Projects/Projects'));
const ProjectsArchive = lazy(() => import('../Projects/ProjectsArchive'));
const ProjectDetail = lazy(() => import('../Projects/ProjectDetail/ProjectDetail'));
const ProjectForm = lazy(() => import('../Projects/ProjectForm/ProjectForm'));
const Cases = lazy(() => import('../Cases/Cases'));
const CasesArchive = lazy(() => import('../Cases/CasesArchive'));
const CaseDetail = lazy(() => import('../Cases/CaseDetail/CaseDetail'));
const CaseForm = lazy(() => import('../Cases/CaseForm/CaseForm'));
const Tasks = lazy(() => import('../Tasks/Tasks'));
const TasksArchive = Tasks;
const TaskForm = lazy(() => import('../Tasks/TaskForm/TaskForm'));
const Calendar = lazy(() => import('../Calendar/Calendar'));
const Reports = lazy(() => import('../Reports/Reports'));
const ReportsPersonal = lazy(() =>
  import('../Reports/ReportsPersonal').then((m) => ({ default: m.ReportsPersonal }))
);
const Admin = lazy(() => import('../Admin/Admin'));
const ViewEmployee = lazy(() => import('../Admin/ViewEmployee'));
const Notifications = lazy(() => import('../Notifications/Notifications'));
const Settings = lazy(() => import('../Settings/Settings'));

// Тестовый пользователь для демонстрации (пока нет бэкенда авторизации)
const testUser: User = {
  id: 1,
  name: 'Хабибуллин Ильдар Ниязович',
  email: 'ildar@example.com',
  role: 'managing_partner',
  is_online: true,
  last_activity: '4 часа назад',
  created_at: '',
  updated_at: '',
};

const Dashboard: React.FC = () => {
  const { user: authUser, loading } = useAuth();
  const location = useLocation();
  const pageTransitionRef = useRef<HTMLDivElement>(null);
  const workplaceSwitchTimeoutRef = useRef<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isWorkplaceSwitching, setIsWorkplaceSwitching] = useState(false);
  
  // Используем тестового пользователя, если нет авторизованного
  const defaultUser = authUser || testUser;
  
  // Определяем активную страницу из URL
  const getActivePage = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') return 'dashboard';
    return path.substring(1); // убираем первый слэш
  };

  const activePage = getActivePage();
  const showSidebar = activePage === 'dashboard';
  
  // Загружаем пользователей для дашборда из кеша React Query (предзагружены в прелоадере)
  const { data: dashboardUsersResponse, isLoading: loadingUsers } = useQuery({
    // Важно: при "зайти под пользователя" меняется авторизованный пользователь,
    // а в бекенде ответ может быть отфильтрован. Поэтому добавляем id в queryKey.
    queryKey: ['dashboardUsers', authUser?.id ?? null],
    queryFn: () => usersService.getDashboardUsers(),
    enabled: showSidebar && !!authUser, // Загружаем только если показываем сайдбар и есть пользователь
    staleTime: 1000 * 30, // 30 секунд
    refetchOnMount: false, // Не обновлять при монтировании, если есть данные в кеше
  });

  const unassignedGroups = React.useMemo(() => {
    if (!dashboardUsersResponse?.unassigned_by_position) return [];
    return dashboardUsersResponse.unassigned_by_position.map((g) => ({
      title: g.title,
      users: (g.users || []).map((u: DashboardUser) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        avatar: u.avatar,
        role: u.role as UserRole,
        is_online: u.is_online,
      })),
    }));
  }, [dashboardUsersResponse]);
  
  // Преобразуем данные из API в формат User
  const dashboardUsers: User[] = React.useMemo(() => {
    if (!dashboardUsersResponse) return [];
    
    const allUsers: User[] = [];

    const normalizeRole = (u: DashboardUser): UserRole => {
      // Защита от неконсистентных данных: если у пользователя нет position_data,
      // не считаем его руководителем/партнёром по role.
      if (!u.position_data) return 'user';
      return (u.role as UserRole) || 'user';
    };

    const normalizePractice = (u: DashboardUser): Practice | undefined => {
      if (u.practice && u.practice.name) {
        return {
          id: u.practice.id ?? 0,
          name: u.practice.name,
        };
      }
      return undefined;
    };
    
    // Управляющие партнеры
    dashboardUsersResponse.managing_partners.forEach((user: DashboardUser) => {
      const teamMembers: User[] = (user.team_members || []).map((member: DashboardUser) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        avatar: member.avatar,
        role: normalizeRole(member),
        positionTitle: member.position_data?.name ?? undefined,
        is_online: member.is_online,
      }));
      allUsers.push({
        id: user.id,
        name: user.name,
        email: user.email,
        avatar: user.avatar,
        role: normalizeRole(user),
        positionTitle: user.position_data?.name ?? undefined,
        practice: normalizePractice(user),
        is_online: user.is_online,
        team_members: teamMembers,
      });
    });
    
    // Партнеры
    dashboardUsersResponse.partners.forEach((user: DashboardUser) => {
      const teamMembers: User[] = (user.team_members || []).map((member: DashboardUser) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        avatar: member.avatar,
        role: normalizeRole(member),
        positionTitle: member.position_data?.name ?? undefined,
        is_online: member.is_online,
      }));
      allUsers.push({
        id: user.id,
        name: user.name,
        email: user.email,
        avatar: user.avatar,
        role: normalizeRole(user),
        positionTitle: user.position_data?.name ?? undefined,
        practice: normalizePractice(user),
        is_online: user.is_online,
        team_members: teamMembers,
      });
    });
    
    // Руководители практик с подчиненными
    dashboardUsersResponse.practice_heads.forEach((user: DashboardUser) => {
      const teamMembers: User[] = (user.team_members || []).map((member: DashboardUser) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        avatar: member.avatar,
        role: normalizeRole(member),
        positionTitle: member.position_data?.name ?? undefined,
        is_online: member.is_online,
      }));
      
      allUsers.push({
        id: user.id,
        name: user.name,
        email: user.email,
        avatar: user.avatar,
        role: normalizeRole(user),
        positionTitle: user.position_data?.name ?? undefined,
        practice: normalizePractice(user),
        is_online: user.is_online,
        team_members: teamMembers,
      });
    });

    // Непривязанные сотрудники (без руководителя) — добавляем в общий список, чтобы по клику открывался их workplace
    (dashboardUsersResponse.unassigned_by_position || []).forEach((group) => {
      (group.users || []).forEach((u: DashboardUser) => {
        allUsers.push({
          id: u.id,
          name: u.name,
          email: u.email,
          avatar: u.avatar,
          role: normalizeRole(u),
          positionTitle: u.position_data?.name ?? undefined,
          is_online: u.is_online,
        });
      });
    });
    
    return allUsers;
  }, [dashboardUsersResponse]);
  
  // Функция для поиска пользователя в основном массиве и в team_members
  const findUserById = (userId: number): User | undefined => {
    // Сначала ищем в основном массиве
    const mainUser = dashboardUsers.find(u => u.id === userId);
    if (mainUser) return mainUser;
    
    // Если не нашли, ищем в team_members всех пользователей
    for (const user of dashboardUsers) {
      if (user.team_members && user.team_members.length > 0) {
        const teamMember = user.team_members.find(member => member.id === userId);
        if (teamMember) return teamMember;
      }
    }
    
    return undefined;
  };
  
  // Рабочий стол из сайдбара: selectedUserId в location.state. Без state — всегда свой дашборд (например, клик по имени в шапке).
  useEffect(() => {
    const st = location.state as { selectedUserId?: number } | null | undefined;
    const raw = st?.selectedUserId;
    const stateUserId =
      typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : undefined;

    if (stateUserId !== undefined) {
      if (stateUserId !== selectedUserId) {
        setIsWorkplaceSwitching(true);
        setSelectedUserId(stateUserId);

        if (workplaceSwitchTimeoutRef.current !== null) {
          window.clearTimeout(workplaceSwitchTimeoutRef.current);
        }

        workplaceSwitchTimeoutRef.current = window.setTimeout(() => {
          setIsWorkplaceSwitching(false);
          workplaceSwitchTimeoutRef.current = null;
        }, 420);
      }
    } else if (selectedUserId !== null) {
      setSelectedUserId(null);
    }
  }, [location.state, selectedUserId]);

  useEffect(() => {
    return () => {
      if (workplaceSwitchTimeoutRef.current !== null) {
        window.clearTimeout(workplaceSwitchTimeoutRef.current);
      }
    };
  }, []);
  
  // Получаем выбранного пользователя
  const selectedUser = selectedUserId 
    ? findUserById(selectedUserId) || defaultUser
    : defaultUser;
  
  const currentUser = selectedUser;

  // Отслеживаем скролл правой части
  useEffect(() => {
    // Находим элемент с классом custom-scrollbar-content внутри dashboard-content
    const pageElement = document.querySelector('.dashboard-content .custom-scrollbar-content') as HTMLElement;
    if (!pageElement) return;

    const handleScroll = () => {
      const scrollTop = pageElement.scrollTop;
      const shouldBeScrolled = scrollTop > 20;
      setIsScrolled(shouldBeScrolled);
    };

    // Добавляем обработчик с опцией passive для лучшей производительности
    pageElement.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Проверяем начальное состояние

    return () => {
      pageElement.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]); // Перезапускаем при смене страницы

  // Не показываем прелоадер здесь - он уже показан в App.tsx
  // Данные предзагружаются в App.tsx, поэтому здесь они уже должны быть в кеше

  return (
    <div className="dashboard-container">
      <Header />
      <div className={`dashboard-content ${isScrolled ? 'scrolled' : ''}`}>
        {showSidebar && (
          <UserSidebar
            users={dashboardUsers}
            unassignedGroups={unassignedGroups}
            loading={loadingUsers}
            currentUserId={authUser?.id ?? null}
          />
        )}
        <CustomScrollbar 
          trackRight={5}
          trackTop={0}
          trackClassName="dashboard-content-scrollbar-track"
        >
          <div 
            key={location.pathname} 
            className="page-transition"
            ref={pageTransitionRef}
          >
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="/dashboard"
                  element={isWorkplaceSwitching ? <PageLoader /> : <Workplace user={currentUser} />}
                />
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/archive" element={<ClientsArchive />} />
                <Route path="/clients/new" element={<ClientForm />} />
                <Route path="/clients/:id/edit" element={<ClientForm />} />
                <Route path="/clients/:id" element={<ClientDetail />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/new" element={<ProjectForm />} />
                <Route path="/projects/:id/edit" element={<ProjectForm />} />
                <Route path="/projects/archive" element={<ProjectsArchive />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/cases" element={<Cases />} />
                <Route path="/cases/new" element={<CaseForm />} />
                <Route path="/cases/archive" element={<CasesArchive />} />
                <Route path="/cases/:id" element={<CaseDetail />} />
                <Route path="/cases/:id/edit" element={<CaseForm />} />
                <Route path="/tasks/archive" element={<TasksArchive />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/tasks/new" element={<TaskForm />} />
                <Route path="/tasks/:id/edit" element={<TaskForm />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/reports/user/:userId" element={<ReportsPersonal />} />
                <Route path="/admin" element={<ProtectedAdminRoute><Admin /></ProtectedAdminRoute>} />
                <Route path="/admin/:section" element={<ProtectedAdminRoute><Admin /></ProtectedAdminRoute>} />
                <Route path="/admin/users/:id" element={<ProtectedAdminRoute><ViewEmployee /></ProtectedAdminRoute>} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/settings" element={<Settings />} />
              </Routes>
            </Suspense>
          </div>
        </CustomScrollbar>
      </div>
    </div>
  );
};

export default Dashboard;

